<template>
	<v-card class="d-flex flex-column fill-height">
		<v-card-text class="pa-0 flex-1-1-100 overflow-hidden">
			<v-container class="pa-0 fill-height" fluid>
				<v-row dense class="fill-height">
					<v-col
						v-if="showList"
						:cols="cols"
						class="fill-height overflow-y-auto"
					>
						<v-list v-model:selected="selection" color="primary">
							<template v-for="(item, index) in files" :key="index">
								<DocumentListItem
									:item="item.value"
									:value="item.value"
								></DocumentListItem>
								<v-divider v-if="index < files.length - 1"></v-divider>
							</template>
						</v-list>
					</v-col>
					<v-col
						:cols="showList ? 12 - cols : 12"
						class="fill-height overflow-hidden"
					>
						<template v-for="item in selection" :key="item.id">
							<ImagePreview
								v-if="
									item &&
									typeof item.id === 'number' &&
									showWhat(item) === 'img'
								"
								:document-id="item.id"
								:download-name="documentName({ id: item.id, file: item })"
								:flat="true"
								:max-height="1000"
								close-btn
								@close="$emit('close')"
							>
								<template v-if="files.length > 1" #prepend>
									<v-btn stacked @click="showList = !showList">
										<v-badge :content="files.length">
											<v-icon icon="far fa-list"></v-icon>
										</v-badge>
									</v-btn>
								</template>
							</ImagePreview>

							<PdfViewer
								v-if="
									item &&
									typeof item.id === 'number' &&
									showWhat(item) === 'pdf'
								"
								:id="item.id"
								:height-factor="1"
								:dialog="false"
								:handle-prev-route="false"
								@close="$emit('close')"
							>
								<template v-if="files.length > 1" #prepend>
									<v-btn stacked @click="showList = !showList">
										<v-badge :content="files.length">
											<v-icon icon="far fa-list"></v-icon>
										</v-badge>
									</v-btn>
								</template>
							</PdfViewer>
						</template>
					</v-col>
				</v-row>
			</v-container>
		</v-card-text>
	</v-card>
</template>

<script setup lang="ts">
import ImagePreview from '@/components/templates/ImagePreview.vue'
import DocumentListItem from '@/components/templates/Modules/Documents/DocumentListItem.vue'
import PdfViewer from '@/components/templates/PdfViewer.vue'
import { useDocumentStore } from '@/stores/modules/document'
import { useDocumentContainerStore } from '@/stores/modules/documentContainer'
import { useToastStore } from '@/stores/system'
import { computed, onBeforeMount, onMounted, ref } from 'vue'
import { useDisplay } from 'vuetify'

type Props = {
	id: number
}

const { id } = defineProps<Props>()

const emit = defineEmits(['close'])

const { documentName, showWhat } = useDocumentStore()
const { toastInfo } = useToastStore()

const loading = ref(true)

const showList = ref(false)
const { smAndUp } = useDisplay()

const cols = computed(() => (smAndUp.value ? 4 : 8))
onMounted(() => {
	showList.value = smAndUp.value && files.value.length > 1
})

const container = ref<Api.DocumentContainer>()

const files = computed(
	() =>
		container.value?.document_files.map((file) => {
			return {
				title: file.file_original_name,
				props: {
					subtitle: file?.document_type?.display_label || file?.document_type?.name,
				},
				value: file,
			}
		}) ?? []
)

const selection = ref<Api.Document[]>()

const { show } = useDocumentContainerStore()

async function load() {
	try {
		loading.value = true
		container.value = (await show({ id: id })) as Api.DocumentContainer

		if (files.value.length === 0) {
			toastInfo({
				message: { key: 'component.documents.noFilesInContainer' },
				timeout: 3000,
			})
			emit('close')
			return
		}

		if (files.value.length > 0) {
			selection.value = [files.value[0].value]
		}
	} catch (error) {
		console.log(error)
	} finally {
		loading.value = false
	}
}

onBeforeMount(async () => {
	await load()
})
</script>

<style scoped lang="scss">
.document-container-view {
	height: 100%;
	display: flex;
	flex-direction: column;
}

.document-container-content {
	flex: 1;
	min-height: 0; // Important for flexbox children
	height: 100%;
}

.full-height {
	height: 100%;
}

.file-list-col {
	height: 100%;
	overflow-y: auto;
}

.preview-col {
	height: 100%;
	overflow: hidden;
}
</style>
